import './home.css';
import {
  fetchEntriesForDate,
  fetchTargets,
  addEntries,
  deleteEntry,
  restoreEntry,
  setEntriesTime,
  setEntriesMeal,
  eatenAtForDay,
  sumTotals,
  type Entry,
  type DailyTargets,
  type MealType,
  type NewEntry,
} from '../services/entries';
import {
  openEditEntry, openEditItem, openAddForMeal, openQuickCalForMeal, openPhotoJob,
  openSearchFromHome, openHomeAction, openPhotoFromHome, type HomeAction,
} from './addSheet';
import { photoQueue, jobReasonShort, jobKind, type PhotoJob } from '../services/photoQueue';
import { foodEmoji } from '../lib/foodEmoji';
import { escapeHtml, toast, undoToast } from '../lib/ui';
import { reportError } from '../lib/sentry';
import { isTransient } from '../lib/netRetry';

// היום המוצג ביומן (ניווט קדימה/אחורה). מנורמל לחצות מקומי.
let selectedDate: Date = startOfDay(new Date());

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** היום שאליו יתועדו הוספות מהבית (נצרך ב-main.ts עבור ה-FAB). */
export function homeAddDate(): Date {
  return selectedDate;
}

const MEAL_EMOJI: Record<MealType, string> = {
  breakfast: '🥣',
  lunch: '🥗',
  dinner: '🍽️',
  snack: '☕',
};

const MEAL_NAME: Record<MealType, string> = {
  breakfast: 'ארוחת בוקר',
  lunch: 'צהריים',
  dinner: 'ערב',
  snack: 'נשנוש',
};

// סדר קיבוץ הארוחות במסך הבית
const MEAL_ORDER: MealType[] = ['breakfast', 'lunch', 'dinner', 'snack'];

function greeting(): string {
  const h = new Date().getHours();
  if (h < 5) return 'לילה טוב';
  if (h < 12) return 'בוקר טוב';
  if (h < 18) return 'צהריים טובים';
  return 'ערב טוב';
}

/** תווית היום הנבחר: היום / אתמול / מחר / יום בשבוע + תאריך */
function dayLabel(d: Date): string {
  const today = startOfDay(new Date());
  if (isSameDay(d, today)) return 'היום';
  if (isSameDay(d, addDays(today, -1))) return 'אתמול';
  if (isSameDay(d, addDays(today, 1))) return 'מחר';
  return new Intl.DateTimeFormat('he-IL', { weekday: 'long', day: 'numeric', month: 'long' }).format(d);
}

const fmt = (n: number): string => Math.round(n).toLocaleString('he-IL');
const fmtTime = (iso: string): string =>
  new Intl.DateTimeFormat('he-IL', { hour: '2-digit', minute: '2-digit' }).format(new Date(iso));

// ---------- רינדור ----------

function dateNavHtml(): string {
  const onToday = isSameDay(selectedDate, startOfDay(new Date()));
  const greet = onToday ? `${greeting()} 🌸` : '';
  return `
    <div class="home-top">
      ${greet ? `<h2 class="home-greet-h">${greet}</h2>` : ''}
      <div class="date-nav">
        <button class="date-arrow" id="date-next" aria-label="יום הבא">‹</button>
        <div class="date-label">
          <b>${dayLabel(selectedDate)}</b>
          ${!onToday ? '<button class="date-today" id="date-today">חזרה להיום</button>' : ''}
        </div>
        <button class="date-arrow" id="date-prev" aria-label="יום קודם">›</button>
      </div>
    </div>`;
}

export function renderHome(): string {
  return `
    <div class="home">
      <div id="home-datenav">${dateNavHtml()}</div>
      <div id="home-queue"></div>
      <div id="home-body">
        <div class="card home-loading">
          <div class="home-skel"></div>
          <div class="home-skel short"></div>
        </div>
      </div>
    </div>`;
}

/** סיכום קומפקטי: טבעת מיני + נשארו/חריגה + מאקרו עם שמות מלאים ומדים */
function sumHtml(entries: Entry[], targets: DailyTargets): string {
  const t = sumTotals(entries);
  const over = t.calories > targets.calories;
  const remaining = Math.abs(targets.calories - t.calories);
  const pctRaw = targets.calories > 0 ? t.calories / targets.calories : 0;
  const deg = Math.round(Math.min(1, pctRaw) * 360);
  const col = over ? 'var(--ring)' : 'var(--accent)';
  const sub = entries.length
    ? `נאכלו ${fmt(t.calories)} מתוך ${fmt(targets.calories)}`
    : 'עוד לא תיעדת היום';
  const sm = (cls: string, name: string, v: number, target: number) => {
    const w = target > 0 ? Math.min(100, (v / target) * 100) : 0;
    return `
      <div class="sm ${cls}">
        <div class="n"><i></i>${name}</div>
        <div class="v">${fmt(v)} <small>/ ${fmt(target)}</small></div>
        <div class="meter"><i style="width:${w}%"></i></div>
      </div>`;
  };
  return `
    <div class="card sum">
      <div class="sum-top">
        <div class="ring-mini" style="background:conic-gradient(${col} 0 ${deg}deg, var(--ring-track) ${deg}deg 360deg)"><i>${Math.round(pctRaw * 100)}%</i></div>
        <div class="sum-mid">
          <div class="a">${over ? `חריגה של ${fmt(remaining)} קק"ל` : `נשארו ${fmt(remaining)} קק"ל`}</div>
          <div class="b">${sub}</div>
        </div>
      </div>
      <div class="sum-macros">
        ${sm('p', 'חלבון', t.proteinG, targets.proteinG)}
        ${sm('c', 'פחמימות', t.carbsG, targets.carbsG)}
        ${sm('f', 'שומן', t.fatG, targets.fatG)}
      </div>
    </div>`;
}

/** שורת רישום: אימוג'י לפי המאכל, שם מלא (עד 2 שורות), כמות+מאקרו+שעה. נגיעה = עריכה. */
function entryRow(e: Entry): string {
  const names = e.items?.length
    ? e.items.map((i) => i.name_he).join(', ')
    : (e.description || 'ארוחה');
  const grams = (e.items ?? []).reduce((a, i) => a + (i.grams ?? 0), 0);
  const qty = e.items?.length === 1 && e.items[0].qty_desc
    ? e.items[0].qty_desc
    : (grams > 0 ? `${fmt(grams)} ג'` : '');
  const macro = e.items?.length
    ? (() => {
        const p = e.items!.reduce((a, i) => a + (i.protein_g ?? 0), 0);
        const c = e.items!.reduce((a, i) => a + (i.carbs_g ?? 0), 0);
        const f = e.items!.reduce((a, i) => a + (i.fat_g ?? 0), 0);
        return `<i class="p"></i>${fmt(p)}<i class="c"></i>${fmt(c)}<i class="f"></i>${fmt(f)}`;
      })()
    : '';
  // מאקרו נבנה כ-HTML גולמי (הנקודות <i> בטוחות — רק מספרים מ-fmt מוזרקים); לכן qty/שעה
  // מסוננים בנפרד לפני החיבור, ולא ה-sub השלם (אחרת ה-<i> היו נסננים לטקסט גלוי).
  const sub = [`${escapeHtml(qty)}${macro}`, escapeHtml(fmtTime(e.eaten_at))].filter(Boolean).join(' · ');
  const icon = (e.items?.length === 1 && e.items[0].icon) || foodEmoji(names);
  return `
    <div class="mrow" data-edit="${e.id}" role="button" tabindex="0">
      <span class="em">${icon}</span>
      <span class="n"><span class="t">${escapeHtml(names)}</span><span class="s">${sub}</span></span>
      <span class="kc">${fmt(e.calories)}</span>
      <button class="row-del" data-del="${e.id}" aria-label="מחיקת ${escapeHtml(names)}">✕</button>
    </div>`;
}

/** קבוצת ארוחה מתועדת: כותרת + ⋯ + שורות + הוספת פריט ישירה (כפתור אחיד — ראה .madd-btn) */
function groupHtml(type: MealType, entries: Entry[]): string {
  const kcal = entries.reduce((a, e) => a + (e.calories ?? 0), 0);
  return `
    <div class="card meal-group" data-group="${type}">
      <div class="mg-head">
        <b class="group-title">${MEAL_EMOJI[type]} ${MEAL_NAME[type]}</b>
        <span class="gk">${fmt(kcal)} קק"ל</span>
        <button class="dots" data-menu="${type}" aria-label="פעולות על הארוחה">⋯</button>
      </div>
      ${entries.map(entryRow).join('')}
      <button class="madd-btn" data-addmeal="${type}">➕ הוספת פריט</button>
    </div>`;
}

/** הצעת "אתמול" לארוחה שטרם תועדה היום — הוספה של כל הארוחה בנגיעה. הדחייה (✕) יושבת
 *  בכותרת ליד ⋯ (לא בתוך תיבת ההצעה) — שתי פעולות-משנה באותו מקום, לא צפוף בפנים. */
function suggestionHtml(type: MealType, yEntries: Entry[]): string {
  const names = [...new Set(yEntries.flatMap((e) =>
    e.items?.length ? e.items.map((i) => i.name_he) : [e.description || 'ארוחה'],
  ))].join(' · ');
  const kcal = yEntries.reduce((a, e) => a + (e.calories ?? 0), 0);
  return `
    <div class="card meal-group sug" data-group="${type}">
      <div class="mg-head">
        <b class="group-title">${MEAL_EMOJI[type]} ${MEAL_NAME[type]}</b>
        <button class="ydismiss" data-ysugdismiss="${type}" aria-label="לא הפעם — התעלמות מההצעה">✕</button>
        <button class="dots" data-menu="${type}" aria-label="פעולות על הארוחה">⋯</button>
      </div>
      <div class="ysug">
        <span class="ytag">אתמול</span>
        <span class="n"><span class="t">${escapeHtml(names)}</span><span class="s">${fmt(kcal)} קק"ל</span></span>
        <button class="plus" data-ysug="${type}" aria-label="הוספה כמו אתמול">+</button>
      </div>
      <button class="madd-btn" data-addmeal="${type}">➕ הוספת מאכל אחר</button>
    </div>`;
}

/** קיבוץ רישומים לפי סוג ארוחה */
function byMeal(entries: Entry[]): Map<MealType, Entry[]> {
  const m = new Map<MealType, Entry[]>();
  for (const e of entries) {
    const type = (e.meal_type ?? 'snack') as MealType;
    const arr = m.get(type) ?? [];
    arr.push(e);
    m.set(type, arr);
  }
  return m;
}

// הצעות האתמול של הרינדור הנוכחי — נצרכות ב-attach (הוספה בנגיעה)
let currentSuggestions: Map<MealType, Entry[]> = new Map();
// הצעות שנדחו ("✕ לא הפעם") — זיכרון-תצוגה בלבד, לא נשמר; מתאפס בשינוי תאריך
const dismissedSuggestions = new Set<MealType>();
let dismissedDateKey = '';

/** קבוצה ריקה (בלי תיעוד היום ובלי "אתמול") — שורת הוספה שקטה + ⋯ (עקבי עם שאר הכרטיסים) */
function emptyGroupHtml(type: MealType): string {
  return `
    <div class="card meal-group empty" data-group="${type}">
      <div class="mg-head">
        <b class="group-title">${MEAL_EMOJI[type]} ${MEAL_NAME[type]}</b>
        <button class="dots" data-menu="${type}" aria-label="פעולות על הארוחה">⋯</button>
      </div>
      <button class="madd-btn" data-addmeal="${type}">➕ הוספה ל${MEAL_NAME[type]}</button>
    </div>`;
}

function mealsHtml(entries: Entry[], yEntries: Entry[], showSuggestions: boolean): string {
  const today = byMeal(entries);
  const yesterday = byMeal(yEntries);
  currentSuggestions = new Map();
  // כל ארבע הארוחות מוצגות תמיד: מתועדת / הצעת אתמול (אלא אם נדחתה) / שורת הוספה ריקה
  const cards: string[] = [];
  for (const type of MEAL_ORDER) {
    const t = today.get(type);
    if (t?.length) {
      cards.push(groupHtml(type, t));
    } else if (showSuggestions && yesterday.get(type)?.length && !dismissedSuggestions.has(type)) {
      currentSuggestions.set(type, yesterday.get(type)!);
      cards.push(suggestionHtml(type, yesterday.get(type)!));
    } else {
      cards.push(emptyGroupHtml(type));
    }
  }
  return cards.join('');
}

function bodyHtml(entries: Entry[], targets: DailyTargets | null, yEntries: Entry[], showSuggestions: boolean): string {
  if (!targets) {
    return `
      <div class="card home-setup">
        <div class="big">👋</div>
        <b>נעים להכיר!</b>
        <p>כדי שנדע כמה קלוריות מגיעות לך ביום — נשלים רגע את הפרופיל.</p>
        <button class="btn-primary" id="home-goto-profile">להשלמת הפרופיל</button>
      </div>`;
  }
  return `
    ${sumHtml(entries, targets)}
    ${searchBarHtml()}
    ${mealsHtml(entries, yEntries, showSuggestions)}`;
}

/** שורת החיפוש בבית: שדה (כפתור בדמות שדה — פותח את עמוד החיפוש עם המקלדת), ומתחתיו כפתורי ה-+
 *  (בר, 04.10.2026) — אותו רכיב ואותו סדר כמו בעמוד החיפוש (.s-act), ועוד שקילה. "חיפוש במאגר" לא
 *  נכנס: השדה שמעל הוא החיפוש. "⚡ קבועים" ירד לכאן מצד השדה ("להוריד אותו למטה"). */
function searchBarHtml(): string {
  const act = (id: string, icon: string, label: string, primary = false) =>
    `<button type="button" class="s-act${primary ? ' primary' : ''}" data-hact="${id}"><div class="ai">${icon}</div><div class="al">${label}</div></button>`;
  return `
    <button type="button" class="home-search" id="home-search">
      <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>
      <span>חיפוש מאכל…</span>
    </button>
    <div class="search-acts home-acts">
      ${act('photo', '📷', 'צילום', true)}${act('regulars', '⚡', 'קבועים')}${act('text', '✏️', 'תיאור')}${act('custom', '➕', 'מאכל שלי')}${act('quick', '🔢', 'מהיר')}${act('weight', '⚖️', 'שקילה')}
    </div>
    <input type="file" id="home-photo" accept="image/*" multiple hidden>`;
}

// ---------- טעינה וחיבור ----------

function renderDateNav(root: HTMLElement): void {
  const nav = root.querySelector('#home-datenav');
  if (!nav) return;
  nav.innerHTML = dateNavHtml();
  nav.querySelector('#date-prev')?.addEventListener('click', () => { selectedDate = addDays(selectedDate, -1); renderDateNav(root); void refresh(root); });
  nav.querySelector('#date-next')?.addEventListener('click', () => { selectedDate = addDays(selectedDate, 1); renderDateNav(root); void refresh(root); });
  nav.querySelector('#date-today')?.addEventListener('click', () => { selectedDate = startOfDay(new Date()); renderDateNav(root); void refresh(root); });
}

async function refresh(root: HTMLElement): Promise<void> {
  const body = root.querySelector<HTMLElement>('#home-body');
  if (!body) return;
  const forDate = selectedDate;
  // הצעות "אתמול" — רק כשמסתכלים על היום הנוכחי
  const showSuggestions = isSameDay(forDate, startOfDay(new Date()));
  // דחיית הצעה נשארת רק כל עוד מסתכלים על אותו תאריך קלנדרי
  const dateKey = forDate.toDateString();
  if (dateKey !== dismissedDateKey) { dismissedDateKey = dateKey; dismissedSuggestions.clear(); }
  // התוכן של יום אחר עוד מוצג תחת הכותרת החדשה עד שהטעינה חוזרת — בקליטה חלשה נגיעה בשורה ערכה
  // רישום מהיום הקודם (ביקורת 29.09). מעמעמים וחוסמים רק במעבר יום, לא ברענון אחרי שמירה (הבהוב).
  const staleDay = !!body.dataset.day && body.dataset.day !== dateKey;
  if (staleDay) { body.classList.add('day-loading'); body.setAttribute('aria-busy', 'true'); }
  const settle = () => { body.classList.remove('day-loading'); body.removeAttribute('aria-busy'); };
  try {
    const [entries, targets, yEntries] = await Promise.all([
      fetchEntriesForDate(forDate),
      fetchTargets(),
      showSuggestions ? fetchEntriesForDate(addDays(forDate, -1)).catch(() => [] as Entry[]) : Promise.resolve([] as Entry[]),
    ]);
    if (!root.isConnected || !isSameDay(forDate, selectedDate)) return; // התחלף היום תוך כדי טעינה
    body.innerHTML = bodyHtml(entries, targets, yEntries, showSuggestions);
    body.dataset.day = dateKey;
    settle();
    attachBody(root, entries);
  } catch {
    if (!root.isConnected || !isSameDay(forDate, selectedDate)) return;
    body.dataset.day = dateKey;
    settle();
    body.innerHTML = `
      <div class="card home-setup">
        <p class="muted">לא הצלחנו לטעון את היום — בדקי חיבור.</p>
        <button class="btn-primary" id="home-retry">ניסיון חוזר</button>
      </div>`;
    body.querySelector('#home-retry')?.addEventListener('click', () => void refresh(root));
  }
}

// נעילת פעולות רקע (הוספת אתמול / מחיקת ארוחה) נגד דאבל-טאפ
let busyAction = false;

/** הוספת כל ארוחת האתמול ליום הנוכחי בנגיעה אחת, עם ביטול */
async function relogYesterday(root: HTMLElement, type: MealType, yEntries: Entry[]): Promise<void> {
  if (busyAction) return;
  busyAction = true;
  try {
    // מדיניות: כל מרכיב = רישום נפרד. רישום לגסי מרובה-מרכיבים מתפצל כאן. סבב רשת אחד לכולם.
    const rows: NewEntry[] = yEntries.flatMap((e) => {
      const items = e.items?.length ? e.items : null;
      if (items && items.length > 1) {
        return items.map((it) => ({
          meal_type: type,
          description: it.name_he,
          items: [it],
          calories: it.calories ?? 0,
          protein_g: it.protein_g ?? null,
          carbs_g: it.carbs_g ?? null,
          fat_g: it.fat_g ?? null,
          source: 'manual' as const,
        }));
      }
      return [{
        meal_type: type,
        description: e.description ?? '',
        items: e.items ?? [],
        calories: e.calories,
        protein_g: e.protein_g,
        carbs_g: e.carbs_g,
        fat_g: e.fat_g,
        source: 'manual' as const,
      }];
    });
    const ids = await addEntries(rows, eatenAtForDay(selectedDate));
    void refresh(root);
    undoToast(`${MEAL_NAME[type]} כמו אתמול נוספה ✓`, () => {
      void Promise.all(ids.map((id) => deleteEntry(id)))
        .then(() => refresh(root))
        .catch(() => toast('הביטול נכשל', 'error'));
    });
  } catch (e) {
    const transient = isTransient(e);
    reportError(e, { action: 'relog_yesterday', transient });
    toast(transient ? 'אין חיבור יציב — נסי שוב עוד רגע' : 'ההוספה נכשלה — נסי שוב', 'error');
  } finally {
    busyAction = false;
  }
}

/**
 * בחירת פריטים מארוחה והעברה/העתקה לארוחה אחרת. נולד מטעות אמיתית: ארוחת ערב שהוזנה
 * בטעות כארוחת בוקר, בלי שום דרך לתקן חוץ ממחיקה והזנה מחדש.
 * **העברה** משנה את meal_type של הרישומים הקיימים; **העתקה** יוצרת חדשים ומשאירה את
 * המקור — למשל אותו קפה שנשתה גם בבוקר וגם אחרי הצהריים.
 */
function openMoveSheet(root: HTMLElement, from: MealType, entries: Entry[]): void {
  document.getElementById('move-sheet')?.remove();
  const sel = new Set<string>(entries.map((e) => e.id));   // ברירת מחדל: הכול מסומן
  const el = document.createElement('div');
  el.id = 'move-sheet';
  el.className = 'move-sheet';

  const render = () => {
    const targets = (['breakfast', 'lunch', 'dinner', 'snack'] as MealType[]).filter((t) => t !== from);
    el.innerHTML = `
      <div class="ms-card">
        <div class="ms-head">
          <b>העברת פריטים מ${MEAL_NAME[from]}</b>
          <button class="ms-x" id="ms-close" aria-label="סגירה">✕</button>
        </div>
        <div class="ms-rows">
          ${entries.map((e) => `
            <label class="ms-row">
              <input type="checkbox" data-mid="${e.id}"${sel.has(e.id) ? ' checked' : ''}>
              <span class="ms-n">${escapeHtml(e.description ?? 'פריט')}</span>
              <span class="ms-k">${fmt(e.calories)}</span>
            </label>`).join('')}
        </div>
        <div class="ms-to">לאן?</div>
        <div class="ms-targets">
          ${targets.map((t) => `<button class="ms-t" data-to="${t}">${MEAL_EMOJI[t]} ${MEAL_NAME[t]}</button>`).join('')}
        </div>
        <label class="ms-copy"><input type="checkbox" id="ms-copy"> להעתיק במקום להעביר (הפריטים יישארו גם כאן)</label>
        <div class="sheet-err" id="ms-err"></div>
      </div>`;

    el.querySelector('#ms-close')!.addEventListener('click', () => el.remove());
    el.querySelectorAll<HTMLInputElement>('[data-mid]').forEach((cb) =>
      cb.addEventListener('change', () => { cb.checked ? sel.add(cb.dataset.mid!) : sel.delete(cb.dataset.mid!); }),
    );
    el.querySelectorAll<HTMLButtonElement>('[data-to]').forEach((b) =>
      b.addEventListener('click', () => void doMove(b.dataset.to as MealType, b)),
    );
  };

  const doMove = async (to: MealType, btn: HTMLButtonElement) => {
    const err = el.querySelector('#ms-err');
    const ids = entries.filter((e) => sel.has(e.id)).map((e) => e.id);
    if (!ids.length) { if (err) err.textContent = 'לא נבחר אף פריט'; return; }
    if (busyAction) return;
    busyAction = true;
    el.querySelectorAll('button').forEach((b) => { b.disabled = true; });
    const copy = (el.querySelector('#ms-copy') as HTMLInputElement | null)?.checked ?? false;
    try {
      if (copy) {
        const src = entries.filter((e) => sel.has(e.id));
        await addEntries(src.map((e) => ({
          meal_type: to, description: e.description, items: e.items,
          calories: e.calories, protein_g: e.protein_g, carbs_g: e.carbs_g, fat_g: e.fat_g,
          source: 'manual' as const,
        })), eatenAtForDay(selectedDate));
      } else {
        await setEntriesMeal(ids, to);
      }
      el.remove();
      void refresh(root);
      toast(`${ids.length === 1 ? 'פריט הועבר' : `${ids.length} פריטים הועברו`} ל${MEAL_NAME[to]} ✓`);
    } catch (e) {
      const transient = isTransient(e);
      reportError(e, { action: copy ? 'copy_items' : 'move_items', transient });
      if (err) err.textContent = transient ? 'אין חיבור יציב — נסי שוב עוד רגע' : 'ההעברה נכשלה — נסי שוב';
      el.querySelectorAll('button').forEach((b) => { b.disabled = false; });
      void btn;
    } finally {
      busyAction = false;
    }
  };

  render();
  document.body.appendChild(el);
}

/** תפריט ⋯ של קבוצת ארוחה: הוספת פריט תמיד; שינוי שעה/מחיקה רק כשיש רישומים אמיתיים
 *  (⋯ עצמו מוצג תמיד — גם על הצעת "אתמול" וגם על ארוחה ריקה, לעקביות) */
function openMealMenu(root: HTMLElement, card: HTMLElement, type: MealType, entries: Entry[]): void {
  document.getElementById('meal-menu')?.remove();
  const menu = document.createElement('div');
  menu.id = 'meal-menu';
  menu.className = 'meal-menu';
  const hasEntries = entries.length > 0;
  menu.innerHTML = `
    <button class="mi" data-act="add">➕ הוספת פריט לארוחה</button>
    <button class="mi" data-act="fav">⭐ מתוך המועדפים</button>
    <button class="mi" data-act="quick">🔢 קלוריות מהירות</button>
    ${hasEntries ? `
    <button class="mi" data-act="move">↔️ העברת פריטים לארוחה אחרת</button>
    <button class="mi" data-act="time">🕐 שינוי שעה</button>
    <button class="mi warn" data-act="del">🗑 מחיקת הארוחה</button>` : ''}`;
  card.appendChild(menu);

  // אם התפריט (נפתח כלפי מטה כברירת מחדל) לא נכנס מעל הניווט התחתון — פותחים אותו כלפי מעלה
  const navEl = document.getElementById('nav');
  const viewportBottom = navEl ? navEl.getBoundingClientRect().top : window.innerHeight;
  if (menu.getBoundingClientRect().bottom > viewportBottom) {
    menu.style.top = 'auto';
    menu.style.bottom = '36px';
  }

  const onDoc = (ev: Event) => { if (!menu.contains(ev.target as Node)) close(); };
  const close = () => { menu.remove(); document.removeEventListener('click', onDoc, true); };
  window.setTimeout(() => document.addEventListener('click', onDoc, true), 0);

  menu.querySelector('[data-act="add"]')!.addEventListener('click', () => {
    close();
    openAddForMeal(type, () => void refresh(root), selectedDate);
  });
  menu.querySelector('[data-act="fav"]')!.addEventListener('click', () => {
    close();
    openAddForMeal(type, () => void refresh(root), selectedDate, 'fav');
  });
  menu.querySelector('[data-act="quick"]')!.addEventListener('click', () => {
    close();
    openQuickCalForMeal(type, () => void refresh(root), selectedDate);
  });

  menu.querySelector('[data-act="move"]')?.addEventListener('click', () => {
    close();
    openMoveSheet(root, type, entries);
  });

  menu.querySelector('[data-act="time"]')?.addEventListener('click', () => {
    const first = new Date(entries[0].eaten_at);
    const hh = String(first.getHours()).padStart(2, '0');
    const mm = String(first.getMinutes()).padStart(2, '0');
    menu.innerHTML = `
      <div class="mi-time">
        <input type="time" id="mm-time" value="${hh}:${mm}">
        <button class="mi-save" id="mm-save">שמירה</button>
      </div>`;
    menu.querySelector('#mm-save')!.addEventListener('click', async () => {
      const v = (document.getElementById('mm-time') as HTMLInputElement).value;
      if (!/^\d\d:\d\d$/.test(v)) return;
      const [H, M] = v.split(':').map(Number);
      const iso = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), H, M).toISOString();
      try {
        await setEntriesTime(entries.map((e) => e.id), iso);
        close();
        void refresh(root);
        toast('השעה עודכנה ✓');
      } catch {
        toast('העדכון נכשל — נסי שוב', 'error');
      }
    });
  });

  menu.querySelector('[data-act="del"]')?.addEventListener('click', async () => {
    close();
    if (busyAction) return;
    busyAction = true;
    try {
      await Promise.all(entries.map((e) => deleteEntry(e.id)));
    } catch {
      toast('המחיקה נכשלה — נסי שוב', 'error');
      void refresh(root);
      busyAction = false;
      return;
    }
    busyAction = false;
    void refresh(root);
    undoToast('הארוחה נמחקה', () => {
      void Promise.all(entries.map((e) => restoreEntry(e)))
        .then(() => refresh(root))
        .catch(() => toast('השחזור נכשל', 'error'));
    });
  });
}

function attachBody(root: HTMLElement, entries: Entry[]): void {
  root.querySelector('#home-goto-profile')?.addEventListener('click', () => {
    document.querySelector<HTMLButtonElement>('.nav-item[data-tab="settings"]')?.click();
  });
  // חיפוש מהבית — ההוספות נרשמות ליום המוצג (כמו ה-+)
  root.querySelector('#home-search')?.addEventListener('click', () => {
    openSearchFromHome(() => void refresh(root), selectedDate);
  });
  // כפתורי ה-+ מתחת לחיפוש. צילום: הבורר נפתח כאן, בתוך הנגיעה (אחרת iOS חוסם), והזרימה — כשנבחרו תמונות
  const photoInput = root.querySelector<HTMLInputElement>('#home-photo');
  photoInput?.addEventListener('change', () => {
    // העתקה לפני האיפוס — אותה תמונה אפשר לבחור שוב בפעם הבאה
    const files = Array.from(photoInput.files ?? []);
    photoInput.value = '';
    if (files.length) openPhotoFromHome(files, () => void refresh(root), selectedDate);
  });
  root.querySelectorAll<HTMLButtonElement>('[data-hact]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const act = btn.dataset.hact!;
      if (act === 'photo') { photoInput?.click(); return; }
      openHomeAction(act as HomeAction, () => void refresh(root), selectedDate);
    });
  });
  // נגיעה על שורת מאכל — עריכה. מנה בודדת → עמוד מאכל (כמו בחיפוש); ארוחה מרובת
  // פריטים / רישום מהיר → מסך עריכת הארוחה.
  root.querySelectorAll<HTMLElement>('[data-edit]').forEach((row) => {
    row.addEventListener('click', () => {
      const entry = entries.find((e) => e.id === row.dataset.edit);
      if (!entry) return;
      if (entry.items?.length === 1) openEditItem(entry, () => refresh(root));
      else openEditEntry(entry, () => refresh(root));
    });
  });
  // ✕ ליד הקלוריות — מחיקת המנה עם ביטול
  root.querySelectorAll<HTMLButtonElement>('[data-del]').forEach((btn) => {
    btn.addEventListener('click', async (ev) => {
      ev.stopPropagation();
      const entry = entries.find((e) => e.id === btn.dataset.del);
      if (!entry) return;
      try {
        await deleteEntry(entry.id);
      } catch {
        toast('המחיקה נכשלה — נסי שוב', 'error');
        return;
      }
      void refresh(root);
      undoToast('המנה נמחקה', () => {
        void restoreEntry(entry).then(() => refresh(root)).catch(() => toast('השחזור נכשל', 'error'));
      });
    });
  });
  // + על הצעת אתמול — הוספת כל הארוחה
  root.querySelectorAll<HTMLButtonElement>('[data-ysug]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const type = btn.dataset.ysug as MealType;
      const yEntries = currentSuggestions.get(type);
      if (yEntries?.length) void relogYesterday(root, type, yEntries);
    });
  });
  // ➕ הוספה (קבוצה ריקה / הוספת פריט לארוחה מתועדת / הוספת מאכל אחר בהצעת אתמול) —
  // כולן פותחות את החיפוש עם סוג הארוחה קבוע מראש
  root.querySelectorAll<HTMLButtonElement>('[data-addmeal]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      openAddForMeal(btn.dataset.addmeal as MealType, () => void refresh(root), selectedDate);
    });
  });
  // ✕ על הצעת "אתמול" — לא הפעם (תצוגה בלבד, מתאפס בשינוי תאריך)
  root.querySelectorAll<HTMLButtonElement>('[data-ysugdismiss]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      dismissedSuggestions.add(btn.dataset.ysugdismiss as MealType);
      void refresh(root);
    });
  });
  // ⋯ — תפריט פעולות לקבוצה (מוצג תמיד: מתועדת / הצעת אתמול / ריקה)
  root.querySelectorAll<HTMLButtonElement>('[data-menu]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const type = btn.dataset.menu as MealType;
      const group = entries.filter((en) => (en.meal_type ?? 'snack') === type);
      const card = btn.closest('.meal-group') as HTMLElement | null;
      if (card) openMealMenu(root, card, type, group);
    });
  });
}

// חציית חצות: PWA שנשאר פתוח שומר selectedDate ישן. בחזרה לפוקוס — אם היום התחלף,
// מאפסים ל"היום" כדי שהוספות לא ייפלו לתאריך הקלנדרי הישן.
let visibilityBound = false;
let activeHomeRoot: HTMLElement | null = null;

function bindVisibilityOnce(): void {
  if (visibilityBound) return;
  visibilityBound = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    const today = startOfDay(new Date());
    if (!isSameDay(selectedDate, today) && activeHomeRoot?.isConnected) {
      selectedDate = today;
      renderDateNav(activeHomeRoot);
      void refresh(activeHomeRoot);
    }
  });
}

// ---------- צילומים בתור (services/photoQueue) ----------
// ⚠️ צילום שהניתוח שלו לא הצליח מיד (גוגל עמוסה / אין קליטה) לא הולך לאיבוד: התור ממשיך
// לנסות לבד, והכרטיס הזה הוא המקום שבו התוצאה מחכה לה. מוצג רק כשיש משהו בתור.

function queueRowHtml(j: PhotoJob): string {
  const p = j.photos[0];
  const d = new Date(j.eatenAtIso);
  const today = startOfDay(new Date());
  const when = isSameDay(d, today) ? fmtTime(j.eatenAtIso)
    : isSameDay(d, addDays(today, -1)) ? `אתמול ${fmtTime(j.eatenAtIso)}`
    : `${d.getDate()}.${d.getMonth() + 1} ${fmtTime(j.eatenAtIso)}`;
  const kind = jobKind(j);
  // תיאור בטקסט: מה שכתבה הוא הכותרת (עם השעה) — כך היא יודעת על איזו ארוחה מדובר
  const title = kind === 'text' ? `${when} · ${escapeHtml(j.text ?? '')}`
    : kind === 'voice' ? `🎙️ הקלטה מ-${when}` : `📷 צילום מ-${when}`;
  let sub = '';
  let action = '';
  if (j.status === 'working') {
    sub = j.attempts ? `${jobReasonShort(j.lastError)} — מנסה שוב עכשיו…` : 'מנתחת עכשיו…';
  } else if (j.status === 'waiting') {
    // בלי כפתור: הניסיון החוזר אוטומטי, וכפתור כאן רק דחק את ההסבר ("ממשי…") ב-375px
    sub = `${jobReasonShort(j.lastError)} — ממשיכה לנסות לבד`;
  } else if (j.status === 'ready' && j.result) {
    const kcal = j.result.items.reduce((a, it) => a + (it.calories ?? 0), 0);
    const n = j.result.items.length;
    sub = `${n === 1 ? 'פריט אחד' : `${n} פריטים`} · ${fmt(kcal)} קק"ל`;
    action = `<button class="pq-go" data-pqopen="${j.id}">לאישור</button>`;
  } else if (j.status === 'clarify') {
    sub = `🤔 ${escapeHtml(j.clarify ?? '')}`;
    action = `<button class="pq-go" data-pqopen="${j.id}">לענות</button>`;
  } else {
    sub = `לא נותח — ${jobReasonShort(j.lastError)}`;
    action = `<button class="pq-go" data-pqnow="${j.id}">לנסות שוב</button>`;
  }
  return `
    <div class="pq-row${j.status === 'ready' ? ' ready' : ''}">
      ${p ? `<img class="pq-thumb" src="data:${p.mime};base64,${p.base64}" alt="">` : `<span class="pq-thumb pq-ico">${kind === 'voice' ? '🎙️' : '✏️'}</span>`}
      <div class="pq-mid">
        <b>${title}</b>
        <span class="pq-sub">${j.status === 'working' ? '<i class="pq-spin"></i>' : ''}${sub}</span>
      </div>
      ${action}
      <button class="pq-x" data-pqdel="${j.id}" aria-label="מחיקת הצילום">✕</button>
    </div>`;
}

function renderQueue(root: HTMLElement): void {
  const el = root.querySelector<HTMLElement>('#home-queue');
  if (!el) return;
  const jobs = photoQueue.list();
  el.innerHTML = jobs.length ? `<div class="card pq">${jobs.map(queueRowHtml).join('')}</div>` : '';
  el.querySelectorAll<HTMLButtonElement>('[data-pqopen]').forEach((b) => b.addEventListener('click', () =>
    openPhotoJob(b.dataset.pqopen!, () => void refresh(root))));
  el.querySelectorAll<HTMLButtonElement>('[data-pqnow]').forEach((b) => b.addEventListener('click', () =>
    photoQueue.retryNow(b.dataset.pqnow!)));
  el.querySelectorAll<HTMLButtonElement>('[data-pqdel]').forEach((b) => b.addEventListener('click', () => {
    const job = photoQueue.take(b.dataset.pqdel!);
    if (job) undoToast('הצילום נמחק', () => photoQueue.restore(job));
  }));
}

let unsubQueue: (() => void) | null = null;

export function attachHome(root: HTMLElement): void {
  activeHomeRoot = root;
  bindVisibilityOnce();
  renderDateNav(root);
  unsubQueue?.();
  unsubQueue = photoQueue.subscribe(() => {
    if (!root.isConnected) { unsubQueue?.(); unsubQueue = null; return; }
    renderQueue(root);
  });
  renderQueue(root);
  void refresh(root);
}
